import React, { useMemo, useState, useEffect } from 'react';
import { useSupabaseStore } from '@/store/SupabaseStore';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Bot, Save, RotateCcw, GripVertical, Edit, RefreshCw } from 'lucide-react';

import { AIModel } from '@/types';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ProviderLogoUploadDialog from './ProviderLogoUploadDialog';
import { supabase } from '@/integrations/supabase/client';
import { createGemma4OnnxModel } from '@/lib/gemma4Onnx';
import { DEFAULT_PROVIDER_ORDER, getProviderLogoOverride, loadModelSettings, saveModelSettings, saveProviderLogoOverride } from '@/lib/modelSettings';
import { getModelIconPath, inferModelIconFilename, resolveModelIconSrc } from '@/lib/modelIcons';
import ModelLogo from '@/components/models/ModelLogo';

interface SortableProviderItemProps {
  id: string;
  displayName: string;
  logo: React.ReactNode;
  onEdit: () => void;
  isEditable: boolean;
  enabled: boolean;
  onToggleEnabled: (enabled: boolean) => void;
}

const SortableProviderItem: React.FC<SortableProviderItemProps> = ({ id, displayName, logo, onEdit, isEditable, enabled, onToggleEnabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 border rounded-md bg-background"
    >
      <div className="flex items-center gap-3">
        <div {...attributes} {...listeners} className="cursor-grab">
          <GripVertical className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="w-6 h-6 flex items-center justify-center">{logo}</div>
        <span className="font-medium">{displayName}</span>
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center space-x-2">
          <Switch
            id={`provider-enabled-${id}`}
            checked={enabled}
            onCheckedChange={onToggleEnabled}
          />
          <Label htmlFor={`provider-enabled-${id}`} className="text-sm">
            {enabled ? 'Enabled' : 'Disabled'}
          </Label>
        </div>
        {isEditable && (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit Logo
          </Button>
        )}
      </div>
    </div>
  );
};

const ModelSettings = () => {
  const { models, updateModel, updateProviderModels, fetchModels } = useSupabaseStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [providerOrder, setProviderOrder] = useState<string[]>(DEFAULT_PROVIDER_ORDER);
  const [disabledProviders, setDisabledProviders] = useState<string[]>([]);
  const [selectedProvider, setSelectedProvider] = useState<{ id: string; name: string } | null>(null);
  const [transformersLogo, setTransformersLogo] = useState('');
  const [isSyncingCatalog, setIsSyncingCatalog] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    const settings = loadModelSettings();
    const currentProviders = new Set(settings.providerOrder);
    const newProviders = DEFAULT_PROVIDER_ORDER.filter((provider) => !currentProviders.has(provider));
    setProviderOrder([...settings.providerOrder, ...newProviders]);
    setDisabledProviders(settings.disabledProviders || []);
    setTransformersLogo(getProviderLogoOverride('transformers'));
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setProviderOrder((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleReset = async () => {
    setProviderOrder(DEFAULT_PROVIDER_ORDER);
    setDisabledProviders([]);
    setTransformersLogo('');
    localStorage.removeItem('modelSettings');
    await fetchModels();
    toast({
      title: 'Settings Reset',
      description: 'Model provider preferences have been reset to default.',
    });
  };

  const handleSave = async () => {
    saveModelSettings({ providerOrder, disabledProviders, providerLogos: { ...(loadModelSettings().providerLogos || {}), ...(transformersLogo ? { transformers: transformersLogo } : {}) } });
    await fetchModels();
    toast({
      title: 'Settings Saved',
      description: 'Model provider preferences have been saved.',
    });
  };

  const handleSyncCatalog = async () => {
    setIsSyncingCatalog(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-ai-model-catalog');
      if (error) throw error;
      await fetchModels();
      toast({
        title: 'Catalog synced',
        description: `Imported ${data?.synced ?? 0} WebLLM catalog models into ai_models.`,
      });
    } catch (error: any) {
      toast({
        title: 'Catalog sync failed',
        description: error?.message || 'Could not sync the remote model catalog.',
        variant: 'destructive',
      });
    } finally {
      setIsSyncingCatalog(false);
    }
  };

  const handleEditLogo = (providerId: string, providerName: string) => {
    setSelectedProvider({ id: providerId, name: providerName });
    setIsDialogOpen(true);
  };

  const handleSaveLogo = async (newIconFilename: string) => {
    if (selectedProvider) {
      try {
        if (selectedProvider.id === 'transformers') {
          const newPath = getModelIconPath(newIconFilename) || '';
          saveProviderLogoOverride('transformers', newPath);
          setTransformersLogo(newPath);
          await fetchModels();
          toast({ title: 'Success', description: `${selectedProvider.name} icon updated.` });
          setIsDialogOpen(false);
          return;
        }

        const normalizedProviderId = selectedProvider.id === 'ollama_remote' ? 'ollama' : selectedProvider.id;
        const localIconPath = getModelIconPath(newIconFilename);

        if (normalizedProviderId === 'maplept') {
          await supabase.functions.invoke('ensure-maplept-model', {
            body: {
              name: 'MaplePT-Mini',
              apiName: '/app/canxpai-maplept-mini-v1',
              tier: 'basic',
              is_active: true,
              icon_filename: newIconFilename,
              logo_url: localIconPath,
            },
          });
        }

        await updateProviderModels(normalizedProviderId, { icon_filename: newIconFilename, logo_url: localIconPath });
        await fetchModels();
        toast({ title: 'Success', description: `${selectedProvider.name} icon updated.` });
      } catch (error) {
      }
    }
    setIsDialogOpen(false);
  };

  const handleToggleActive = async (model: AIModel, isActive: boolean) => {
    try {
      await updateModel(model.id, { is_active: isActive });
      toast({
        title: 'Model Updated',
        description: `${model.name} has been ${isActive ? 'enabled' : 'disabled'}.`,
      });
    } catch (error) {
    }
  };

  const handleToggleProvider = (providerId: string, enabled: boolean) => {
    setDisabledProviders((current) => {
      if (enabled) {
        return current.filter((provider) => provider !== providerId);
      }
      if (current.includes(providerId)) {
        return current;
      }
      return [...current, providerId];
    });
  };

  const providerDisplayMap: Record<string, { name: string; defaultLogoSrc?: string; fallbackLogo: React.ReactNode; isEditable: boolean }> = {
    'cohere': { name: 'Cohere', defaultLogoSrc: '/images/models/cohere.svg', fallbackLogo: <Bot className="h-4 w-4 text-muted-foreground" />, isEditable: true },
    'openai': { name: 'OpenAI', defaultLogoSrc: '/images/models/openai.svg', fallbackLogo: <Bot className="h-4 w-4 text-muted-foreground" />, isEditable: true },
    'claude': { name: 'Claude', defaultLogoSrc: '/images/models/claude.svg', fallbackLogo: <Bot className="h-4 w-4 text-muted-foreground" />, isEditable: true },
    'gemini': { name: 'Gemini', defaultLogoSrc: '/images/models/gemini.svg', fallbackLogo: <Bot className="h-4 w-4 text-muted-foreground" />, isEditable: true },
    'deepseek': { name: 'DeepSeek', defaultLogoSrc: '/images/models/deepseek.svg', fallbackLogo: <Bot className="h-4 w-4 text-muted-foreground" />, isEditable: true },
    'maplept': { name: 'MaplePT', defaultLogoSrc: '/images/models/maplept.svg', fallbackLogo: <Bot className="h-4 w-4 text-muted-foreground" />, isEditable: true },
    'ollama_remote': { name: 'Ollama', fallbackLogo: <Bot className="h-4 w-4 text-blue-600" />, isEditable: true },
    'ollama_local': { name: 'Local Ollama', fallbackLogo: <Bot className="h-4 w-4 text-green-600" />, isEditable: false },
    'custom': { name: 'Custom', fallbackLogo: <Bot className="h-4 w-4 text-muted-foreground" />, isEditable: false },
    'local': { name: 'MaplePT-Free', fallbackLogo: <Bot className="h-4 w-4 text-green-600" />, isEditable: false },
    'transformers': { name: 'Gemma 4', defaultLogoSrc: '/images/models/gemma.svg', fallbackLogo: <Bot className="h-4 w-4 text-amber-500" />, isEditable: true },
  };

  const getProviderLogo = (provider: string) => {
    const displayInfo = providerDisplayMap[provider];
    const overrideLogoUrl = provider === 'transformers' ? transformersLogo : '';

    if (overrideLogoUrl) {
      return (
        <ModelLogo
          src={overrideLogoUrl}
          alt={displayInfo.name}
          sizeClass="h-6 w-6"
          fallback={displayInfo.fallbackLogo}
        />
      );
    }

    const normalizedProvider = provider === 'ollama_remote' || provider === 'ollama_local'
      ? 'ollama'
      : provider === 'local' || provider === 'transformers'
        ? undefined
        : provider;
    const modelForProvider = normalizedProvider ? models.find((model) => model.provider === normalizedProvider) : undefined;
    const resolvedLogo = modelForProvider ? resolveModelIconSrc(modelForProvider) : displayInfo.defaultLogoSrc;

    return (
      <ModelLogo
        src={resolvedLogo}
        alt={displayInfo.name}
        sizeClass="h-6 w-6"
        fallback={displayInfo.fallbackLogo}
      />
    );
  };

  const adminModels = useMemo(() => {
    const gemmaModel = createGemma4OnnxModel();
    const nonOllamaModels = models.filter((model) => model.provider !== 'ollama');

    if (nonOllamaModels.some((model) => model.id === gemmaModel.id)) {
      return nonOllamaModels;
    }

    return [...nonOllamaModels, gemmaModel].sort((a, b) => a.name.localeCompare(b.name));
  }, [models, transformersLogo]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Model Provider Preferences</CardTitle>
          <CardDescription>
            Drag to reorder providers, enable or disable provider groups, and choose their icons from local bundled assets. Your preferences are saved locally.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 mb-4">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={providerOrder}
                strategy={verticalListSortingStrategy}
              >
                {providerOrder.map((provider) => {
                  const displayInfo = providerDisplayMap[provider];
                  if (!displayInfo) return null;
                  return (
                    <SortableProviderItem
                      key={provider}
                      id={provider}
                      displayName={displayInfo.name}
                      logo={getProviderLogo(provider)}
                      isEditable={displayInfo.isEditable}
                      enabled={!disabledProviders.includes(provider)}
                      onToggleEnabled={(enabled) => handleToggleProvider(provider, enabled)}
                      onEdit={() => handleEditLogo(provider, displayInfo.name)}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Preferences
            </Button>
            <Button variant="outline" onClick={handleReset}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Default
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <CardTitle>AI Model Management</CardTitle>
              <CardDescription>
                Enable or disable specific AI models available in the application. Runtime-only models like Gemma 4 ONNX are managed locally.
              </CardDescription>
            </div>
            <Button variant="outline" onClick={handleSyncCatalog} disabled={isSyncingCatalog}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isSyncingCatalog ? 'animate-spin' : ''}`} />
              {isSyncingCatalog ? 'Syncing catalog...' : 'Sync remote catalog'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {adminModels.map((model) => {
              const isGemma = model.provider === 'transformers';
              const isEnabled = isGemma ? !disabledProviders.includes('transformers') : !!model.is_active;

              return (
                <div key={model.id} className="flex items-center justify-between p-3 border rounded-md">
                  <div className="flex items-center gap-4">
                    <ModelLogo
                      src={resolveModelIconSrc(model)}
                      alt={model.name}
                      sizeClass="h-9 w-9"
                      fallback={<Bot size={20} />}
                    />
                    <div>
                      <p className="font-semibold">{model.name}</p>
                      <p className="text-sm text-muted-foreground">{model.runtime || model.provider}{model.family ? ` • ${model.family}` : ''}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center space-x-2">
                      <Switch
                        id={`active-switch-${model.id}`}
                        checked={isEnabled}
                        onCheckedChange={(checked) => {
                          if (isGemma) {
                            handleToggleProvider('transformers', checked);
                            return;
                          }
                          void handleToggleActive(model, checked);
                        }}
                      />
                      <Label htmlFor={`active-switch-${model.id}`} className="text-sm">
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </Label>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {selectedProvider && (
        <ProviderLogoUploadDialog
          isOpen={isDialogOpen}
          onOpenChange={setIsDialogOpen}
          providerName={selectedProvider.name}
          currentIconFilename={selectedProvider.id === 'transformers'
            ? (transformersLogo.split('/').pop() || 'gemma.svg')
            : (() => {
                const selectedModel = models.find(m => m.provider === (selectedProvider.id === 'ollama_remote' ? 'ollama' : selectedProvider.id));
                return selectedModel?.icon_filename || inferModelIconFilename(selectedModel || { provider: selectedProvider.id as AIModel['provider'], name: selectedProvider.name }) || '';
              })()}
          onSave={handleSaveLogo}
        />
      )}

    </div>
  );
};

export default ModelSettings;